import { useEffect, useState } from 'react'

import { synthesizeAssistantSpeech } from '@/lib/assistant-api'

export interface AssistantSpeechState {
	isSpeaking: boolean
	isLoading: boolean
	messageId: string | null
}

type SpeechListener = (state: AssistantSpeechState) => void

let activeAudio: HTMLAudioElement | null = null
let activeObjectUrl: string | null = null
let speechState: AssistantSpeechState = {
	isSpeaking: false,
	isLoading: false,
	messageId: null,
}
const listeners = new Set<SpeechListener>()

function notifyListeners() {
	for (const listener of listeners) {
		listener(speechState)
	}
}

function setSpeechState(partial: Partial<AssistantSpeechState>) {
	speechState = { ...speechState, ...partial }
	notifyListeners()
}

function cleanupActiveSpeech() {
	if (activeAudio) {
		activeAudio.pause()
		activeAudio.onended = null
		activeAudio.onerror = null
		activeAudio = null
	}
	if (activeObjectUrl) {
		URL.revokeObjectURL(activeObjectUrl)
		activeObjectUrl = null
	}
}

export function useAssistantSpeechState(): AssistantSpeechState {
	const [state, setState] = useState(speechState)

	useEffect(() => {
		listeners.add(setState)
		return () => {
			listeners.delete(setState)
		}
	}, [])

	return state
}

export function isAssistantSpeechActive(): boolean {
	return speechState.isSpeaking || speechState.isLoading
}

export function stopAssistantSpeech() {
	cleanupActiveSpeech()
	setSpeechState({ isSpeaking: false, isLoading: false, messageId: null })
}

export async function speakAssistantText(
	text: string,
	options?: { messageId?: string },
): Promise<void> {
	stopAssistantSpeech()

	const trimmed = text.trim()
	if (!trimmed) return

	setSpeechState({ isLoading: true, isSpeaking: false, messageId: options?.messageId ?? null })

	try {
		const blob = await synthesizeAssistantSpeech(trimmed)
		const url = URL.createObjectURL(blob)
		activeObjectUrl = url

		const audio = new Audio(url)
		activeAudio = audio
		setSpeechState({ isLoading: false, isSpeaking: true, messageId: options?.messageId ?? null })

		await new Promise<void>((resolve, reject) => {
			audio.onended = () => {
				cleanupActiveSpeech()
				setSpeechState({ isSpeaking: false, isLoading: false, messageId: null })
				resolve()
			}
			audio.onerror = () => {
				cleanupActiveSpeech()
				setSpeechState({ isSpeaking: false, isLoading: false, messageId: null })
				reject(new Error('Failed to play speech'))
			}

			void audio.play().catch((err) => {
				cleanupActiveSpeech()
				setSpeechState({ isSpeaking: false, isLoading: false, messageId: null })
				reject(err)
			})
		})
	} catch (err) {
		setSpeechState({ isSpeaking: false, isLoading: false, messageId: null })
		throw err
	}
}
